import type { DxToolRegistry } from '@kerberos-compliance/compliance-shared-ui/dx';
import { DxLevelMap, DxToolKindMap, DxToolStatusMap } from '@kerberos-compliance/compliance-shared-ui/dx';

/**
 * The single source of tool identity. `DxToolId` is derived from these keys, so
 * adding a tool is a one-place edit and a mistyped id is a compile error.
 *
 * See `docs/dx-tooling.md` for the level semantics and the review process.
 */
export const dxToolRegistry = {
  'sentry-trigger': {
    label: 'Sentry Error Trigger',
    kind: DxToolKindMap.Actuator,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'Does an error of this shape reach Sentry, and with which normalization?',
    owner: 'platform-team',
    reviewBy: '2026-11-01',
    ticket: 'ITI-933',
  },
  'session-inspector': {
    label: 'Session & Inactivity Inspector',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.Impersonation,
    status: DxToolStatusMap.Implemented,
    answers: 'Why was this user logged out, and is the inactivity tick drifting?',
    owner: 'support-team',
    reviewBy: '2027-02-01',
    ticket: 'ITI-933',
  },
  'diagnostics-bundle': {
    // Renders the shared status panel, which also answers "which build is this, against
    // which backend?" - the former `build-badge` tool, now the panel's Build section.
    label: 'Application Status & Diagnostics Bundle',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.Impersonation,
    status: DxToolStatusMap.Implemented,
    answers: 'Which build is this, and what does an engineer need to act on the ticket without a round trip?',
    owner: 'support-team',
    reviewBy: '2027-03-01',
    ticket: 'ITI-933',
  },
  'auth-explainer': {
    label: 'Grants & IAM Roles',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.InternalRole,
    status: DxToolStatusMap.Implemented,
    answers: 'Why does this user see (or not see) this feature?',
    owner: 'platform-team',
    reviewBy: '2027-03-01',
    ticket: 'ITI-933',
  },
  'state-snapshot': {
    label: 'Store & Signal Snapshot',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'What do the root stores hold right now?',
    owner: 'platform-team',
    reviewBy: '2027-04-01',
    ticket: 'ITI-933',
  },
  'network-interceptor': {
    label: 'Network Edge-Case Interceptor',
    kind: DxToolKindMap.Actuator,
    level: DxLevelMap.LocalOnly,
    status: DxToolStatusMap.Implemented,
    answers: 'How does the UI behave on 503/429/403 and slow responses, without touching the backend?',
    owner: 'qa-team',
    reviewBy: '2027-05-01',
    ticket: 'ITI-933',
  },
  'a11y-i18n-inspector': {
    label: 'Accessibility & i18n Diagnostic',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'Which translation keys are missing at runtime, and where is focus right now?',
    owner: 'qa-team',
    // Renewed 2026-10-02: still backs the a11y panel and the runtime missing-translation sink.
    reviewBy: '2027-07-01',
    ticket: 'ITI-933',
  },
  'hanko-inspector': {
    label: 'Hanko & Passkey Inspector',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'Which Hanko tenant and passkey configuration is this session talking to?',
    owner: 'security-team',
    reviewBy: '2027-05-01',
    ticket: 'ITI-933',
  },
  'hanko-flow-timeline': {
    label: 'Hanko Flow Timeline',
    kind: DxToolKindMap.Inspector,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'Which flow states did this login walk through, and which one errored?',
    owner: 'security-team',
    reviewBy: '2027-01-01',
    ticket: 'ITI-933',
  },
  'storage-inspector': {
    label: 'Storage Keys & State Reset',
    // Actuator: it clears individual keys and bulk-clears auth storage.
    kind: DxToolKindMap.Actuator,
    level: DxLevelMap.NonProduction,
    status: DxToolStatusMap.Implemented,
    answers: 'Is this a stale-storage bug, and does clearing the key fix it?',
    owner: 'platform-team',
    reviewBy: '2027-06-01',
    ticket: 'ITI-933',
  },
} as const satisfies DxToolRegistry;

export type DxToolId = keyof typeof dxToolRegistry;

export const dxToolIds = Object.keys(dxToolRegistry) as DxToolId[];
